import {
  ChangeDetectionStrategy,
  Component,
  computed, effect,
  EnvironmentInjector,
  inject, makeStateKey,
  runInInjectionContext,
  signal, TransferState
} from '@angular/core';
import {AsyncPipe, JsonPipe, NgClass} from "@angular/common";
import {Image} from "primeng/image";
import {ButtonModule} from "primeng/button";
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ParamMap,
  Router,
  RouterLink
} from "@angular/router";
import {UserService} from "../../services/user.service";
import {filter, map, of} from "rxjs";
import {AuthService} from "../../services/auth/auth.service";
import {RouteDataService} from "../../services/route-data.service";
import {initialSignupRoute} from '../../pages/signup/specific-signup.routes';
import {BackButtonComponent} from "./back-button/back-button.component";
import {HeaderButtonsComponent} from "./header-buttons/header-buttons.component";
import {PaymentService} from "../../services/payment/payment.service";
import {ABService} from "../../services/a-b.service";
import {getDeepestChildRoute, isNative} from "../../utils";
import {SignupButtonComponent} from "../signup-button/signup-button.component";
import {is} from "typia";
import {PlatformService} from "../../services/platform.service";
import {HeaderStyle} from "../../app.routes";

const HEADER_STYLE_KEY = makeStateKey<HeaderStyle>('header-style');

@Component({
  selector: 'homey-header',
  standalone: true,
  imports: [
    Image,
    ButtonModule,
    NgClass,
    BackButtonComponent,
    HeaderButtonsComponent,
    AsyncPipe,
    SignupButtonComponent,
    RouterLink,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  protected headerType: 'grey' | 'default' = 'default';

  protected readonly abService = inject(ABService);
  protected readonly activatedRoute = inject(ActivatedRoute);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly platformService = inject(PlatformService);
  private readonly transferState = inject(TransferState);

  protected readonly user$ = this.userService.user$;
  protected readonly userSignal = this.userService.user;
  protected readonly lastUserStatus = this.userService.lastUserStatus;
  protected readonly showHeader = computed(
  () => this.lastUserStatus() === 'unauthorized' && false
  );
  private readonly paymentService = inject(PaymentService);

  protected loading = false;
  private readonly params = signal<ParamMap | undefined>(undefined);
  protected readonly signupToApplyRoute = computed(
    () => this.routeDataService.routeData().signupToApplyRoute ?? initialSignupRoute
  )

  protected readonly loaded = signal(false);

  protected readonly signupToApplyRouteIncludingInfo = computed(
    () => {
      const params = this.params();

      if (!params) {
        return of({});
      }

      return runInInjectionContext(this.environmentInjector, () => this.routeDataService.routeData().signupToApplyRouteParams?.(params) ?? of({}));
    }
  )

  protected readonly signupToApplyText = computed(
    () => this.routeDataService.routeData().signupToApplyText ?? 'Try for free'
  )

  protected readonly headerStyle = computed(
    () => {
      if(!this.loaded()) {
        if(this.platformService.isBrowser) {
          return this.transferState.get(HEADER_STYLE_KEY, 'loading');
        }
        return 'loading';
      } else {
        return this.routeDataService.routeData().headerStyle ?? 'sub-page'
      }
    }
  )

  protected readonly resubscribeDiscount = this.abService.resubscribeDiscount;
  protected readonly hideWarningHeader = computed(() => this.routeDataService.routeData().hideWarningHeader ?? false);


  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly routeDataService: RouteDataService
    ) {

    effect(() => {
      const headerStyle = this.headerStyle();
      if(this.platformService.isServer) {
        this.transferState.set(HEADER_STYLE_KEY, headerStyle);
      }
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => {
          this.loaded.set(true);
          let route = this.activatedRoute;
          // let data = {};
          while (route.firstChild) {
            route = route.firstChild;
            // data = {...data, ...route.snapshot.data};
          }

          this.params.set(getDeepestChildRoute(route).snapshot.paramMap);

          if (route.snapshot.data['headerType']) {
            this.headerType = route.snapshot.data['headerType'];
          } else {
            this.headerType = 'default';
          }
        })
      )
      .subscribe();

    this.router.events.subscribe((e) => {
      this.navigationInterceptor(e);
    })
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.loading = true
    }
    if (event instanceof NavigationEnd) {
      this.loading = false
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loading = false
    }
    if (event instanceof NavigationError) {
      this.loading = false
    }
  }

  public onResubscribeClick() {
    void this.router.navigate(['/resubscribe']);
  }

  protected readonly initialSignupRoute = initialSignupRoute;
  protected readonly is = is;
  protected readonly isNative = isNative;
}
